import type { SceneContent } from '../../presentation/types';

/** Source: docs/product-content/CARBONMOVE.md (CarbonMove Whitepaper v1.0). */
export const carbonProof: SceneContent = {
  id: 'carbon-proof',
  shortLabel: 'CARBONMOVE',
  title: 'CarbonMove — Small Actions, Real Impact',
  seconds: 35,
  brand: 'carbonmove',
  bridgeIn: (context) => `${context.isEnabled('mint') || context.isEnabled('research-space') ? 'Moderta also builds independent products.' : 'Moderta builds independent products.'} One starts with everyday behaviour.`,
  atmosphere: (step) => ({ pattern: step === 1 ? 'inward' : 'drift', density: 0.2, opacity: 0.06, cameraZ: 14 }),
  script: {
    title: 'CarbonMove — Small Actions, Real Impact',
    onScreen: ['WALKING · CYCLING · PUBLIC TRANSPORT · RECYCLING', 'SMALL ACTIONS.', 'REAL IMPACT.', 'BUT HOW DO YOU PROVE IT?', 'CarbonMove', 'Turning verified sustainable action into measurable climate impact.'],
    visual: 'Four people-scale actions in a row: walking, cycling, public transport, recycling. Lines rise from each into one shared impact. Question marks appear on every connection. CarbonMove is revealed over the dimmed row.',
    narration: [
      'Every day, people make small sustainable choices. Walking instead of driving. Cycling. Taking public transport. Recycling.',
      'Added up, those actions have real environmental value.',
      'But most of them disappear as soon as they happen. They are not measured, they are hard to verify, and a claim is easy to fake. So if an action creates impact, how do you prove it?',
      'That is where CarbonMove starts. It turns verified sustainable action into measurable climate impact.',
    ],
    presenterNotes: [
      'Four actions appear. "Small actions."',
      'They rise into one impact. "Real impact."',
      'Question marks. Slow down: "But how do you prove it?"',
      'Reveal CarbonMove. Not a fitness tracker, not a corporate dashboard.',
    ],
  },
};

export const carbonVerify: SceneContent = {
  id: 'carbon-verify',
  shortLabel: 'VERIFY',
  title: 'CarbonMove — Climate Action You Can Verify',
  seconds: 55,
  brand: 'carbonmove',
  bridgeIn: 'So how does CarbonMove prove an action?',
  atmosphere: (step) => ({ pattern: step >= 3 ? 'orbit' : 'stream', density: 0.16, opacity: 0.05, cameraZ: 14.4 }),
  script: {
    title: 'CarbonMove — Climate Action You Can Verify',
    onScreen: ['CYCLING', 'GPS · MOTION · TIME · CONTEXT', 'ONE ACTION. MANY SIGNALS.', 'PROOF', 'PROVE THE ACTION, NOT THE RAW DATA.', 'VERIFIED', 'MEASURABLE IMPACT', 'REWARD', 'RECORDED', 'COMMIT · VERIFY · REWARD', 'CLIMATE ACTION YOU CAN VERIFY.'],
    visual: 'A cycling trip on the left. Four signal chips (GPS, motion, time, context) show illustrative raw values. They merge into a proof, and their values are masked. Five validators cross-check around a ring and a tick appears. The result branches into measurable impact and a reward, and small record blocks line up along the bottom.',
    narration: [
      'Take one bike ride. The phone captures signals from it: location, motion, time and context.',
      'Those signals are combined into a proof of the action. The whitepaper describes privacy-preserving proofs, so what is shared is that the action happened, not the raw sensor data behind it.',
      'Then independent checks look at it: motion-pattern analysis, fraud detection and validators cross-checking one another. The system calls this Proof of Commitment. Only when they agree is the action verified.',
      'A verified action is converted into an estimate of the emissions it avoided, rewarded, and recorded on CarbonMove\'s own chain, so the record cannot quietly change afterwards.',
      'Commit, verify, reward. Climate action you can verify.',
    ],
    presenterNotes: [
      'The ride and its signals. "One action, many signals." Values are illustrative.',
      'Signals merge; values mask. "Prove the action, not the raw data."',
      'Validators agree. "Proof of Commitment." Keep the blockchain detail in the narration.',
      'Impact, reward, record. "Verified action becomes verified value."',
      '"Climate action you can verify."',
    ],
  },
};
